import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const MODES = [
  {
    id: 'text-to-game',
    icon: 'Gamepad2',
    label: 'Text to Game',
    desc: 'Full playable game',
    color: 'var(--color-primary)',
    tooltip: 'Generate a complete playable game from your description',
  },
  {
    id: 'prompt-to-mini-game',
    icon: 'Zap',
    label: 'Mini Game',
    desc: 'Quick arcade game',
    color: 'var(--color-secondary)',
    tooltip: 'Create a quick arcade or puzzle mini game',
  },
  {
    id: 'text-to-animation',
    icon: 'Film',
    label: 'Animation',
    desc: 'Animated scene',
    color: 'var(--color-accent)',
    tooltip: 'Generate an animated scene or sequence',
  },
  {
    id: 'script-to-video',
    icon: 'Video',
    label: 'Script to Video',
    desc: 'Cinematic video',
    color: '#10B981',
    tooltip: 'Turn your script or scene description into a cinematic video',
  },
  {
    id: 'music-to-video',
    icon: 'Music',
    label: 'Music to Video',
    desc: 'Music visualizer',
    color: '#F43F5E',
    tooltip: 'Upload music and generate synchronized visuals',
  },
  {
    id: 'text-to-music',
    icon: 'Music2',
    label: 'Text to Music',
    desc: 'AI music gen',
    color: '#8B5CF6',
    tooltip: 'Generate original AI music from a text description',
  },
  {
    id: 'lip-sync',
    icon: 'Mic',
    label: 'Lip Sync',
    desc: 'Talking character',
    color: '#EC4899',
    tooltip: 'Animate a face or character with voice — talking character video',
  },
  {
    id: 'image-to-video',
    icon: 'ImagePlay',
    label: 'Img to Video',
    desc: 'Animate image',
    color: '#06B6D4',
    tooltip: 'Upload a single image and animate it with cinematic effects',
  },
];

export default function ModeSelector({ selectedMode, onModeChange, disabled }) {
  const [hoveredMode, setHoveredMode] = useState(null);

  return (
    <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-4 gap-2 md:gap-2">
      {MODES?.map((mode) => {
        const active = selectedMode === mode?.id;
        const hovered = hoveredMode === mode?.id;
        return (
          <div key={mode?.id} className="relative">
            <button
              onClick={() => !disabled && onModeChange(mode?.id)}
              disabled={disabled}
              aria-pressed={active}
              onMouseEnter={() => setHoveredMode(mode?.id)}
              onMouseLeave={() => setHoveredMode(null)}
              className="w-full flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderColor: active ? mode?.color : 'var(--color-border)',
                background: active ? `${mode?.color}14` : hovered ? `${mode?.color}08` : 'var(--color-card)',
                boxShadow: active ? `0 0 0 2px ${mode?.color}40` : hovered ? `0 2px 8px ${mode?.color}20` : 'none',
                transform: active ? 'translateY(-1px)' : hovered ? 'translateY(-1px)' : 'translateY(0)',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300"
                style={{ background: active ? mode?.color : `${mode?.color}20` }}
              >
                <Icon name={mode?.icon} size={16} color={active ? '#fff' : mode?.color} strokeWidth={2} />
              </div>
              <span
                className="text-xs font-semibold text-center leading-tight transition-colors duration-200"
                style={{ color: active ? mode?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)', fontSize: 10 }}
              >
                {mode?.label}
              </span>
              <span className="text-xs text-center hidden sm:block" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>
                {mode?.desc}
              </span>
              {(mode?.id === 'lip-sync' || mode?.id === 'image-to-video') && (
                <span className="text-xs px-1 py-0.5 rounded" style={{ background: `${mode?.color}20`, color: mode?.color, fontFamily: 'var(--font-caption)', fontSize: 8, fontWeight: 700 }}>NEW</span>
              )}
              {mode?.id === 'text-to-music' && (
                <span className="text-xs px-1 py-0.5 rounded" style={{ background: '#8B5CF620', color: '#8B5CF6', fontFamily: 'var(--font-caption)', fontSize: 8, fontWeight: 700 }}>NEW</span>
              )}
            </button>
            {/* Tooltip */}
            {hovered && !disabled && (
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 px-3 py-2 rounded-lg text-xs text-center whitespace-nowrap pointer-events-none"
                style={{
                  background: 'var(--color-foreground)',
                  color: 'var(--color-background)',
                  boxShadow: 'var(--shadow-lg)',
                  maxWidth: 180,
                  whiteSpace: 'normal',
                  fontFamily: 'var(--font-caption)',
                }}
              >
                {mode?.tooltip}
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
                  style={{
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: `5px solid var(--color-foreground)`,
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}